"use client";

import { cn } from "@jesus-central-church/shared";
import { Menu, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { colors } from "../../lib/colors";
import { Link } from "../link";

export interface MenuItem {
  label: string;
  href: string;
  target?: string;
}

export interface MenuSection {
  title: string;
  items: MenuItem[];
}

export interface SocialLink {
  icon: React.ReactNode;
  label: string;
  href: string;
}

export interface CinematicMenuProperties extends Record<string, unknown> {
  menuSections?: MenuSection[];
  socialLinks?: SocialLink[];
  colorScheme?: {
    name: string;
    accentColor: string;
    accentRgba: string;
    hoverShadow: string;
  };
  className?: string;
}

export function CinematicMenu({
  menuSections = [],
  socialLinks = [],
  colorScheme,
  className,
  ...additionalProperties
}: CinematicMenuProperties) {
  const [isOpen, setIsOpen] = useState(false);

  const defaultColorScheme = {
    name: colors.STRATEGY_GOLD,
    accentColor: "#c89657",
    accentRgba: "rgba(200, 150, 87, 0.1)",
    hoverShadow: "rgba(200, 150, 87, 0.5)",
  };

  const activeColorScheme = colorScheme || defaultColorScheme;

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  return (
    <>
      <button
        key={activeColorScheme.name}
        onClick={handleToggle}
        aria-label={isOpen ? "Close menu" : "Open menu"}
        className={cn(
          "group bg-freedom/10 border-freedom/20 relative z-[110] flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border backdrop-blur-md transition-all duration-500 hover:scale-110 sm:h-12 sm:w-12 md:h-14 md:w-14 lg:h-14 lg:w-14",
          className,
        )}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = activeColorScheme.accentColor;
          e.currentTarget.style.boxShadow = `0 0 30px ${activeColorScheme.hoverShadow}`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "";
          e.currentTarget.style.boxShadow = "";
        }}
        {...additionalProperties}
      >
        <div className="relative h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7">
          <Menu
            className={cn(
              "text-freedom absolute inset-0 h-5 w-5 transition-all duration-300 sm:h-6 sm:w-6 md:h-7 md:w-7",
              isOpen
                ? "scale-0 rotate-90 opacity-0"
                : "scale-100 rotate-0 opacity-100",
            )}
          />
          <X
            className={cn(
              "text-freedom absolute inset-0 h-5 w-5 transition-all duration-300 sm:h-6 sm:w-6 md:h-7 md:w-7",
              isOpen
                ? "scale-100 rotate-0 opacity-100"
                : "scale-0 -rotate-90 opacity-0",
            )}
          />
        </div>
      </button>

      <div
        className={cn(
          "bg-boldness fixed inset-0 z-[100] transition-all duration-700",
          isOpen
            ? "scale-100 opacity-100"
            : "pointer-events-none scale-95 opacity-0",
        )}
      >
        <div
          className="absolute inset-0 transition-all duration-700"
          style={{
            background: `radial-gradient(circle at 50% 50%, ${activeColorScheme.accentRgba}, transparent 50%)`,
          }}
        />

        <div className="relative h-full w-full overflow-y-auto">
          <div className="container mx-auto px-6 pt-32 pb-20 md:pt-40">
            <div className="mb-20 grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-16">
              {menuSections.map((section, sectionIndex) => (
                <div
                  key={sectionIndex}
                  className="menu-section transform transition-all duration-700 ease-out"
                  style={{
                    opacity: isOpen ? 1 : 0,
                    transform: isOpen
                      ? "translateY(0) rotateX(0)"
                      : "translateY(60px) rotateX(-20deg)",
                    transitionDelay: `${sectionIndex * 200 + 200}ms`,
                  }}
                >
                  <h3
                    className="mb-6 text-sm font-semibold tracking-wider uppercase"
                    style={{ color: activeColorScheme.accentColor }}
                  >
                    {section.title}
                  </h3>
                  <ul className="space-y-4">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex}>
                        <Link
                          href={item.href}
                          target={
                            item.target ||
                            (item.href.includes("http") ? "_blank" : "_self")
                          }
                          className="group text-freedom inline-block text-3xl font-bold transition-all duration-300 md:text-4xl lg:text-5xl"
                          onClick={handleToggle}
                          onMouseEnter={(
                            e: React.MouseEvent<HTMLAnchorElement>,
                          ) => {
                            const span = e.currentTarget.querySelector("span");
                            if (span) {
                              (span as HTMLElement).style.color =
                                activeColorScheme.accentColor;
                              (span as HTMLElement).style.textShadow =
                                `0 0 30px ${activeColorScheme.hoverShadow}`;
                            }
                          }}
                          onMouseLeave={(
                            e: React.MouseEvent<HTMLAnchorElement>,
                          ) => {
                            const span = e.currentTarget.querySelector("span");
                            if (span) {
                              (span as HTMLElement).style.color = "";
                              (span as HTMLElement).style.textShadow = "";
                            }
                          }}
                        >
                          <span className="font-heading inline-block uppercase transition-all duration-300 group-hover:translate-x-2 group-hover:scale-105">
                            {item.label}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div
              className="menu-footer border-freedom/10 transform border-t pt-12 transition-all duration-700 ease-out"
              style={{
                opacity: isOpen ? 1 : 0,
                transform: isOpen ? "translateY(0)" : "translateY(30px)",
                transitionDelay: "500ms",
              }}
            >
              <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
                {socialLinks.length > 0 && (
                  <div>
                    <p className="text-freedom/70 mb-4">Vind ons op</p>
                    <div className="flex flex-wrap gap-6 md:flex-nowrap">
                      {socialLinks.map((link, index) => (
                        <Link
                          key={index}
                          href={link.href}
                          target={
                            link.href.includes("http") ? "_blank" : "_self"
                          }
                          className="text-freedom text-lg font-medium transition-colors duration-300"
                          onClick={handleToggle}
                          onMouseEnter={(
                            e: React.MouseEvent<HTMLAnchorElement>,
                          ) => {
                            e.currentTarget.style.color =
                              activeColorScheme.accentColor;
                          }}
                          onMouseLeave={(
                            e: React.MouseEvent<HTMLAnchorElement>,
                          ) => {
                            e.currentTarget.style.color = "";
                          }}
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                <div className="text-freedom/70 text-sm">
                  <p>© {new Date().getFullYear()} <Link href="https://jesuscentral.church" target="_blank" rel="noopener noreferrer">Jesus Central Church</Link></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
